import type { DirectiveBinding } from 'vue';

type TooltipBinding =
  | string
  | {
      text?: string;
      enabled?: boolean;
      /** true면 DOM 말줄임 없이도 툴팁 표시(호버 시에만) */
      force?: boolean;
      /** 툴팁에 노출할 최대 글자 수 */
      maxLength?: number;
    };

type TooltipState = {
  mouseEnter: (event: MouseEvent) => void;
  mouseLeave: () => void;
};

type TooltipHTMLElement = HTMLElement & {
  __ellipsisTooltipState__?: TooltipState;
  __ellipsisTooltipBindingValue__?: TooltipBinding;
};

const TOOLTIP_ID = 'sg-ellipsis-tooltip';
const TOOLTIP_STYLE_ID = 'sg-ellipsis-tooltip-style';
const TOOLTIP_OFFSET = 8;
const VIEWPORT_MARGIN = 8;

const createTooltipStyle = () => {
  if (document.getElementById(TOOLTIP_STYLE_ID)) return;

  const styleEl = document.createElement('style');
  styleEl.id = TOOLTIP_STYLE_ID;
  styleEl.textContent = `
    #${TOOLTIP_ID} {
      position: fixed;
      z-index: 10080;
      min-width: 120px;
      max-width: min(460px, 65vw);
      padding: 8px 10px;
      border-radius: 4px;
      background: #3f495b;
      color: #ffffff;
      font-size: 12px;
      line-height: 1.4;
      white-space: normal;
      word-break: break-all;
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
      pointer-events: none;
      --sg-tooltip-arrow-left: 24px;
    }

    #${TOOLTIP_ID}::before {
      content: '';
      position: absolute;
      left: var(--sg-tooltip-arrow-left);
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
    }

    #${TOOLTIP_ID}[data-placement='bottom']::before {
      top: -6px;
      border-bottom: 6px solid #3f495b;
    }

    #${TOOLTIP_ID}[data-placement='top']::before {
      bottom: -6px;
      border-top: 6px solid #3f495b;
    }
  `;
  document.head.appendChild(styleEl);
};

const getTooltipElement = () => {
  let tooltipEl = document.getElementById(TOOLTIP_ID);
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = TOOLTIP_ID;
    document.body.appendChild(tooltipEl);
  }
  return tooltipEl as HTMLDivElement;
};

const getBindingText = (el: TooltipHTMLElement) => {
  const currentValue = el.__ellipsisTooltipBindingValue__;
  let raw = '';
  if (typeof currentValue === 'string') raw = currentValue;
  else if (currentValue && typeof currentValue.text === 'string') raw = currentValue.text;
  else raw = el.textContent?.trim() ?? '';

  if (
    typeof currentValue === 'object' &&
    currentValue !== null &&
    typeof currentValue.maxLength === 'number' &&
    currentValue.maxLength > 0 &&
    raw.length > currentValue.maxLength
  ) {
    const max = currentValue.maxLength;
    const suffix = '...';
    const bodyLen = max - suffix.length;
    // 앞쪽 텍스트를 보여 주고, 뒤가 잘렸음을 말줄임으로 표시 (총 max자)
    raw = raw.slice(0, Math.max(0, bodyLen)) + suffix;
  }
  return raw;
};

const isForceTooltip = (el: TooltipHTMLElement) => {
  const currentValue = el.__ellipsisTooltipBindingValue__;
  return typeof currentValue === 'object' && currentValue !== null && currentValue.force === true;
};

const isTooltipEnabled = (el: TooltipHTMLElement) => {
  const currentValue = el.__ellipsisTooltipBindingValue__;
  if (typeof currentValue === 'object' && currentValue !== null && currentValue.enabled === false) {
    return false;
  }
  return true;
};

const isEllipsisApplied = (el: HTMLElement) => {
  return el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
};

const hideTooltip = () => {
  const tooltipEl = document.getElementById(TOOLTIP_ID);
  if (tooltipEl) {
    tooltipEl.remove();
  }
};

const showTooltip = (targetEl: HTMLElement, text: string) => {
  if (!text) return;

  const tooltipEl = getTooltipElement();
  tooltipEl.textContent = text;
  tooltipEl.dataset.placement = 'bottom';

  const targetRect = targetEl.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();

  const hasBottomSpace = targetRect.bottom + TOOLTIP_OFFSET + tooltipRect.height + VIEWPORT_MARGIN <= window.innerHeight;
  const placement = hasBottomSpace ? 'bottom' : 'top';
  tooltipEl.dataset.placement = placement;

  const unclampedLeft = targetRect.left;
  const maxLeft = window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(unclampedLeft, maxLeft));
  tooltipEl.style.left = `${left}px`;

  const top =
    placement === 'bottom'
      ? targetRect.bottom + TOOLTIP_OFFSET
      : targetRect.top - tooltipRect.height - TOOLTIP_OFFSET;
  tooltipEl.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`;

  // 툴팁 박스가 뷰포트에 맞춰 가로로 밀릴 때도 화살표가 타깃 중앙을 가리키도록 함
  const tooltipRectAfter = tooltipEl.getBoundingClientRect();
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const arrowLeft = Math.min(Math.max(targetCenterX - left, 12), tooltipRectAfter.width - 18);
  tooltipEl.style.setProperty('--sg-tooltip-arrow-left', `${arrowLeft}px`);
};

export default defineNuxtPlugin(nuxtApp => {
  if (process.server) return;

  createTooltipStyle();

  nuxtApp.vueApp.directive('ellipsis-tooltip', {
    mounted(el: TooltipHTMLElement, binding: DirectiveBinding<TooltipBinding>) {
      el.__ellipsisTooltipBindingValue__ = binding.value;

      const mouseEnter = () => {
        if (!isTooltipEnabled(el)) {
          hideTooltip();
          return;
        }

        const text = getBindingText(el);
        const showWithoutEllipsis = isForceTooltip(el);
        if (!text || (!showWithoutEllipsis && !isEllipsisApplied(el))) {
          hideTooltip();
          return;
        }
        showTooltip(el, text);
      };

      const mouseLeave = () => {
        hideTooltip();
      };

      el.addEventListener('mouseenter', mouseEnter);
      el.addEventListener('mouseleave', mouseLeave);
      el.__ellipsisTooltipState__ = { mouseEnter, mouseLeave };
    },
    updated(el: TooltipHTMLElement, binding: DirectiveBinding<TooltipBinding>) {
      el.__ellipsisTooltipBindingValue__ = binding.value;
      if (!isForceTooltip(el) && !isEllipsisApplied(el)) {
        hideTooltip();
      }
    },
    unmounted(el: TooltipHTMLElement) {
      const state = el.__ellipsisTooltipState__;
      if (state) {
        el.removeEventListener('mouseenter', state.mouseEnter);
        el.removeEventListener('mouseleave', state.mouseLeave);
      }
      hideTooltip();
      delete el.__ellipsisTooltipBindingValue__;
      delete el.__ellipsisTooltipState__;
    },
  });
});
